export const LIFE_BASE_ELIGIBLE_SERVICE_KEYS = ["telemedicina", "aps_fisica", "aps_digital"] as const;

export type LifeBaseEligibleServiceKey = (typeof LIFE_BASE_ELIGIBLE_SERVICE_KEYS)[number];

export const LIFE_BASE_MULTI_COMPANY_COLUMN_KEY = "empresa_destino_cnpj" as const;
export const LIFE_BASE_MULTI_COMPANY_COLUMN_LABEL = "Empresa de destino (CNPJ)" as const;
export const LIFE_BASE_HOLDER_CPF_COLUMN_KEY = "cpf_titular" as const;
export const LIFE_BASE_HOLDER_CPF_COLUMN_LABEL = "CPF Titular" as const;
export const LIFE_BASE_RELATIONSHIP_COLUMN_KEY = "grau_parentesco" as const;
export const LIFE_BASE_RELATIONSHIP_COLUMN_LABEL = "Grau de parentesco" as const;

export const LIFE_BASE_TEMPLATE_TYPES = {
  SINGLE_COMPANY: "standard",
  MULTI_COMPANY: "multi_company",
} as const;

export const LIFE_BASE_BENEFICIARY_TYPES = {
  HOLDER: "titular",
  DEPENDENT: "dependente",
} as const;

export const LIFE_BASE_IMPORT_MAX_ROWS = 20000 as const;

export function getLifeBaseImportMaxRowsMessage(rowCount?: number | null) {
  const normalizedRowCount =
    typeof rowCount === "number" && Number.isFinite(rowCount)
      ? rowCount.toLocaleString("pt-BR")
      : null;
  const limitLabel = LIFE_BASE_IMPORT_MAX_ROWS.toLocaleString("pt-BR");

  return [
    "Não foi possível continuar com a importação porque o arquivo excede o limite suportado pelo sistema.",
    `Para garantir estabilidade no processamento, envie arquivos com até ${limitLabel} linhas.`,
    normalizedRowCount ? `O arquivo enviado possui ${normalizedRowCount} linhas.` : null,
    "Divida a planilha em lotes menores e tente novamente.",
  ]
    .filter(Boolean)
    .join(" ");
}

export type LifeBaseBeneficiaryType =
  (typeof LIFE_BASE_BENEFICIARY_TYPES)[keyof typeof LIFE_BASE_BENEFICIARY_TYPES];

export const LIFE_BASE_RELATIONSHIP_CODES = [
  "TITULAR",
  "CONJUGE",
  "MARITAL",
  "FILHO(A)",
  "PAI",
  "MAE",
  "PAI/MAE",
  "SOGRO(A)",
  "OUTRO(A)",
  "ENTEADO(A)",
  "IRMAO(A)",
  "AGREGADO(A)",
  "NETO",
  "GUARDA",
] as const;

export type LifeBaseRelationshipCode = (typeof LIFE_BASE_RELATIONSHIP_CODES)[number];

export const LIFE_BASE_RELATIONSHIP_LABELS: Record<LifeBaseRelationshipCode, string> = {
  TITULAR: "TITULAR",
  CONJUGE: "CÔNJUGE",
  MARITAL: "MARITAL",
  "FILHO(A)": "FILHO(A)",
  PAI: "PAI",
  MAE: "MÃE",
  "PAI/MAE": "PAI/MÃE",
  "SOGRO(A)": "SOGRO(A)",
  "OUTRO(A)": "OUTRO(A)",
  "ENTEADO(A)": "ENTEADO(A)",
  "IRMAO(A)": "IRMÃO(A)",
  "AGREGADO(A)": "AGREGADO(A)",
  NETO: "NETO",
  GUARDA: "GUARDA",
};

export const CORRELATED_PORTAL_MODULES = [
  {
    moduleKey: "telemedicina",
    serviceKey: "telemedicina",
    serviceName: "Telemedicina",
    moduleName: "Telemedicina",
    description:
      "Frente preparada para indicadores assistenciais, jornadas clínicas, utilização e gestão operacional de teleatendimento.",
  },
  {
    moduleKey: "aps_fisica",
    serviceKey: "aps_fisica",
    serviceName: "APS Física",
    moduleName: "APS Física",
    description:
      "Frente preparada para gestão territorial, acompanhamento presencial, coordenação do cuidado e indicadores de APS física.",
  },
  {
    moduleKey: "aps_digital",
    serviceKey: "aps_digital",
    serviceName: "APS Digital",
    moduleName: "APS Digital",
    description:
      "Frente preparada para jornada digital de saúde, monitoramento remoto, engajamento e inteligência operacional de APS digital.",
  },
] as const;

export function isLifeBaseEligibleServiceKey(value?: string | null): value is LifeBaseEligibleServiceKey {
  return Boolean(value && LIFE_BASE_ELIGIBLE_SERVICE_KEYS.includes(value as LifeBaseEligibleServiceKey));
}

export function getLifeBaseRelationshipLabel(value?: string | null) {
  if (!value) return "-";
  return LIFE_BASE_RELATIONSHIP_LABELS[value as LifeBaseRelationshipCode] ?? value;
}
